import { useCallback, useEffect, useMemo, useState } from 'react';
import fireabse from 'firebase/app';
import { useCollectionOnce } from 'react-firebase-hooks/firestore';
import { useAuth } from '../../hooks/useAuth';
import { INVITE_ALL_USERS } from '../../constants/post';
import { markPostAsRead } from '../../firebase/api/post';

const POSTS_COLLECTION_NAME = 'posts';

export const usePostNotifications = () => {
    const {
        currentUser: { email = '', uid = '' },
    } = useAuth();

    const [pendingPostsCount, setPendingPostsCount] = useState(0);
    const [invitationsPostIds, setInvitationsPostIds] = useState([]);
    const [publicInvitationsPostIds, setPublicInvitationsPostIds] = useState([]);
    const [unreadPostIds, setUnreadPostIds] = useState([]);

    const [pendingPostsSnap] = useCollectionOnce(
        fireabse
            .firestore()
            .collection(POSTS_COLLECTION_NAME)
            // .where('isDeleted', '==', false)
            // .where('isApprovedByAdmin', '==', false)
            .where('isReviewedByAdmin', '==', false)
    );

    useEffect(() => {
        if (pendingPostsSnap) {
            setPendingPostsCount(pendingPostsSnap.size);
        }
    }, [pendingPostsSnap]);

    const [invitationsPostsSnap] = useCollectionOnce(
        fireabse
            .firestore()
            .collection(POSTS_COLLECTION_NAME)
            .where('isDeleted', '==', false)
            .where('inviteColleagues', 'array-contains-any', [email, INVITE_ALL_USERS])
    );

    useEffect(() => {
        if (invitationsPostsSnap) {
            setInvitationsPostIds(
                invitationsPostsSnap.docs
                    .filter(d => d.data().publisherUid !== uid)
                    .filter(d => !(d.data().viewed || []).includes(email))
                    .map(d => d.id)
            );
        }
    }, [invitationsPostsSnap, email]);

    const [personalInvitationsPostsSnap] = useCollectionOnce(
        fireabse
            .firestore()
            .collection(POSTS_COLLECTION_NAME)
            .where('isDeleted', '==', false)
            .where('inviteColleagues', 'array-contains-any', [email])
    );

    let personalInvitationsPostIds = useMemo(() => {
        if (personalInvitationsPostsSnap) {
            return personalInvitationsPostsSnap.docs
                .filter(d => !(d.data().viewed || []).includes(email))
                .map(d => d.id);
        } else return [];
    }, [personalInvitationsPostsSnap, email]);

    const [publicInvitationsPostsSnap] = useCollectionOnce(
        fireabse
            .firestore()
            .collection(POSTS_COLLECTION_NAME)
            .where('isDeleted', '==', false)
            .where('inviteColleagues', 'array-contains-any', [INVITE_ALL_USERS])
        // .where('isApprovedByAdmin', '==', true)
    );

    useEffect(() => {
        if (publicInvitationsPostsSnap) {
            setPublicInvitationsPostIds(
                publicInvitationsPostsSnap.docs
                    .filter(d => d.data().publisherUid !== uid)
                    .filter(d => !(d.data().viewed || []).includes(email))
                    .map(d => d.id)
            );
        }
    }, [publicInvitationsPostsSnap, email]);

    const viewInvitationPost = useCallback(
        async postId => {
            const postSnap = await fireabse
                .firestore()
                .collection(POSTS_COLLECTION_NAME)
                .doc(postId)
                .get();
            const { viewed = [] } = postSnap.data();

            if (!viewed.includes(email)) {
                setInvitationsPostIds(value => value.filter(p => p !== postId));

                return fireabse
                    .firestore()
                    .collection(POSTS_COLLECTION_NAME)
                    .doc(postId)
                    .update({
                        viewed: [...viewed, email],
                    });
            }
        },
        [email, invitationsPostIds]
    );

    const [unreadMyPostsSnap] = useCollectionOnce(
        fireabse
            .firestore()
            .collection(POSTS_COLLECTION_NAME)
            .where('isApprovedByAdmin', '==', true)
            .where('isDeleted', '==', false)
            .where('publisherUid', '==', uid)
            .where('hasUpdates', '==', true)
    );

    useEffect(() => {
        if (unreadMyPostsSnap) {
            setUnreadPostIds(unreadMyPostsSnap.docs.map(d => d.id));
        }
    }, [unreadMyPostsSnap]);

    const decreasePostCount = useCallback(
        async postId => {
            if (unreadPostIds.includes(postId)) {
                await markPostAsRead(postId);
                setUnreadPostIds(unreadPostIds.filter(d => d !== postId));
            }
        },
        [unreadPostIds]
    );

    const decreasePendingPostCount = useCallback(() => {
        if (pendingPostsCount > 0) {
            setPendingPostsCount(pendingPostsCount - 1);
        }
    }, [pendingPostsCount]);

    return {
        unreadPostCount: unreadPostIds.length,
        unreadPostIds,
        invitationsPostCount: invitationsPostIds.length,
        invitationsPostIds,
        personalInvitationsPostCount: personalInvitationsPostIds.length,
        personalInvitationsPostIds,
        publicInvitationsPostCount: publicInvitationsPostIds.length,
        publicInvitationsPostIds,
        pendingPostsCount,
        decreasePendingPostCount,
        viewInvitationPost,
        decreasePostCount,
    };
};
